import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  FooterActionBar,
  Icon,
  InlineMessage,
  ModalIsland,
} from '@saviynt/design-system';
import cx from 'classnames';

import { type MenuId } from '../../../models';
import {
  findAccessibleMenuLinks,
  H3,
  type InvertedLocalize,
  type Localize,
  P,
  type StateSetter,
  useCookies,
  useInvertedLocalize,
  useLocalize,
} from '../../../utilities';
import { type PersonalShortcuts } from '../models';
import {
  type AppDispatch,
  thunks,
  trySavePersonalShortcuts,
  useDispatch,
  useSelector,
} from '../store';

import illustrations from './illustrations';
import {
  invertedMessages,
  optionDescriptions,
  personalizationSectionMessages,
} from './messages';
import personalizationSections, {
  type PersonalizationSection,
} from './personalizationSections';

import './PersonalizationModal.css';

interface PersonalizationModalProps {
  isOpen: boolean;
  isSmallTabletFormFactor: boolean;
  onClose: () => void;
}

const UNUSABLE_PERSONAL_SHORTCUT_VALUES: (PersonalShortcuts | undefined)[] = [
  'error',
  undefined,
];
const UNUSABLE_DEFAULT_SHORTCUT_VALUES: (
  | MenuId[]
  | 'unauthorized'
  | undefined
)[] = ['unauthorized', undefined];
const MAX_SELECTION_COUNT = 4;

type SelectedShortcutId = Set<MenuId> | null;

/**
 * Renders the personalization modal and persists to backend.
 */
export default function PersonalizationModal({
  isOpen,
  isSmallTabletFormFactor,
  onClose: handleClose,
}: PersonalizationModalProps): JSX.Element {
  const l = useInvertedLocalize(invertedMessages);
  const localize = useLocalize();
  const dispatch = useDispatch();
  const [{ userKey }] = useCookies('userKey');
  const sideMenu = useSelector((s) => s.app.sideMenu);
  const menuList = useSelector((s) => s.profile.menuList);
  const allShortcutOptions = useSelector(
    (state) => state.user.homepage.quicklinksOptions
  );
  const authorizedShortcutOptions = useMemo(() => {
    const links =
      findAccessibleMenuLinks({
        userKey,
        sideMenu,
        menuList,
        menuIds: allShortcutOptions,
      }) ?? [];

    return links.reduce((acc, link) => acc.add(link.id), new Set<MenuId>());
  }, [allShortcutOptions, menuList, sideMenu, userKey]);
  const defaultShortcutIds = useSelector((s) => s.user.homepage.quicklinks);
  const savedPersonalShortcuts = useSelector(
    (s) => s.shortcuts.personalShortcuts
  );
  const [selectedShortcutIds, setSelectedShortcutIds] =
    useState<Set<MenuId> | null>(null);
  const [isModified, setIsModified] = useState(false);
  const [isDefaultSelection, setIsDefaultSelection] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isError, setIsError] = useState(false);
  const className = cx(
    'Nav_ShortcutsPersonalizationModal',
    isSmallTabletFormFactor && 'Nav_ShortcutsPersonalizationModal--singleColumn'
  );
  const areActionsDisabled =
    savedPersonalShortcuts !== 'error' &&
    selectedShortcutIds !== null &&
    isSaving;
  const contentBody = createContentBody(
    savedPersonalShortcuts,
    selectedShortcutIds,
    setSelectedShortcutIds,
    authorizedShortcutOptions,
    l,
    localize,
    setIsModified
  );
  const footerActionBar = createFooterActionBar(
    setSelectedShortcutIds,
    selectedShortcutIds,
    setIsSaving,
    setIsError,
    savedPersonalShortcuts,
    dispatch,
    handleClose,
    areActionsDisabled,
    isError,
    l,
    selectedShortcutIds?.size === MAX_SELECTION_COUNT,
    isSmallTabletFormFactor,
    setIsModified,
    isModified,
    isDefaultSelection
  );

  usePersistedDataEffect(
    savedPersonalShortcuts,
    selectedShortcutIds,
    defaultShortcutIds,
    isOpen,
    setSelectedShortcutIds,
    setIsModified,
    setIsDefaultSelection
  );

  return (
    <ModalIsland
      isOpen={isOpen}
      isMobileWidth={isSmallTabletFormFactor}
      className={className}
      title={l('Which shortcuts do you need?')}
      subtitle={l(
        'Get quick access to time-sensitive actions like certification assignments, access requests, password change notices and more. These shortcuts appear as tiles at the top of your homepage and are shown in the order they are selected.'
      )}
      headerIcon={<Icon kind='Settings' />}
      onClose={handleClose}
      contentBody={contentBody}
      FooterActionBarComp={footerActionBar}
    />
  );
}

function createContentBody(
  savedPersonalShortcuts: PersonalShortcuts | undefined,
  selectedShortcutIds: Set<MenuId> | null,
  setSelectedShortcutIds: StateSetter<SelectedShortcutId>,
  authorizedShortcutOptions: Set<MenuId>,
  l: InvertedLocalize<keyof typeof invertedMessages>,
  localize: Localize,
  setIsModified: StateSetter<boolean>
) {
  if (savedPersonalShortcuts === 'error') {
    return <P>{l('There was an error fetching personal shortcuts.')}</P>;
  }

  if (selectedShortcutIds === null) {
    return <P>{l('Loading...')}</P>;
  }

  const selectedIds = new Set(selectedShortcutIds);
  const disabled = selectedIds.size >= MAX_SELECTION_COUNT;

  const handleChange = (checked: boolean, value: string) => {
    if (checked) {
      selectedIds.add(value as MenuId);
    } else {
      selectedIds.delete(value as MenuId);
    }

    setSelectedShortcutIds(selectedIds);
    setIsModified(true);
  };

  const mySections = Object.entries(personalizationSections).reduce<
    Record<PersonalizationSection, MenuId[]>
  >(
    (acc, [section, links]) => {
      acc[section as PersonalizationSection] = links.filter((link) =>
        authorizedShortcutOptions.has(link)
      );

      return acc;
    },
    {
      'request-access': [],
      'view-and-monitor': [],
      'manage-and-update': [],
      'passwords-and-security': [],
    }
  );

  return (
    <>
      {Object.entries(mySections).map(([section, links]) => {
        const sectionKey = `Nav.ShortcutsWidget.Personalization.Modal.SectionTitle--${section}`;

        return links.length === 0 ? null : (
          <section
            key={section}
            className='Nav_ShortcutsPersonalizationModal-section'>
            <H3 className='Nav_ShortcutsPersonalizationModal-section-title'>
              {localize({
                id: sectionKey,
                defaultMessage: personalizationSectionMessages[sectionKey],
              })}
            </H3>

            {links.map((option) => {
              const labelKey =
                option === 'custom:start-access-request'
                  ? 'NewRequestHome.QuickLink.AccessRequest.Label'
                  : `gen2.leftnav.label.${option}`;
              const LeftIcon = illustrations[option];
              const descriptionKey = `Nav.ShortcutsWidget.Personalization.Modal.Description--${option}`;
              const description =
                optionDescriptions[descriptionKey] === undefined
                  ? undefined
                  : localize({
                      id: descriptionKey,
                      defaultMessage: optionDescriptions[descriptionKey],
                    });

              return (
                <Checkbox
                  kind='multiLine'
                  label={localize(labelKey)}
                  onChange={() => {
                    handleChange(!selectedIds.has(option), option);
                  }}
                  labelLineLimit={1}
                  className='Nav_ShortcutsPersonalizationModal-section-tile'
                  isChecked={selectedIds.has(option)}
                  isDisabled={disabled && !selectedIds.has(option)}
                  key={option}
                  LeftIcon={
                    <LeftIcon className='Nav_ShortcutsPersonalizationModal-section-tile-icon' />
                  }
                  description={description}
                />
              );
            })}
          </section>
        );
      })}
    </>
  );
}

function createFooterActionBar(
  setSelectedShortcutIds: StateSetter<SelectedShortcutId>,
  selectedShortcutIds: SelectedShortcutId,
  setIsSaving: StateSetter<boolean>,
  setIsError: StateSetter<boolean>,
  savedPersonalShortcuts: PersonalShortcuts | undefined,
  dispatch: AppDispatch,
  handleClose: () => void,
  areActionsDisabled: boolean,
  isError: boolean,
  l: InvertedLocalize<keyof typeof invertedMessages>,
  isMax: boolean,
  isSmallTabletFormFactor: boolean,
  setIsModified: StateSetter<boolean>,
  isModified: boolean,
  isDefaultSelection: boolean
) {
  const handleClearClick = () => {
    setIsModified(true);
    setSelectedShortcutIds(new Set());
  };

  const handleSubmitClick = () =>
    save(selectedShortcutIds === null ? null : Array.from(selectedShortcutIds));
  const handleUseDefaultsClick = () => save(null);

  const save = (shortcutIds: MenuId[] | null) =>
    (async () => {
      setIsSaving(true);
      setIsError(false);

      const version =
        typeof savedPersonalShortcuts === 'object'
          ? savedPersonalShortcuts.version
          : undefined;
      const isSuccessful = await trySavePersonalShortcuts(shortcutIds, version);

      setIsSaving(false);

      if (isSuccessful) {
        dispatch(thunks.requestPersonalShortcuts());

        handleClose();
      } else {
        setIsError(true);
      }
    })().catch(() => setIsError(true));

  return (
    <FooterActionBar className='Nav_ShortcutsPersonalizationModal-footer'>
      <Button
        isDisabled={areActionsDisabled}
        type='button'
        kind='subtle'
        onClick={handleClearClick}>
        {l('Clear')}
      </Button>
      {isMax && !isSmallTabletFormFactor && (
        <InlineMessage
          colorTheme='info'
          className='Nav_ShortcutsPersonalizationModal-footer-message'
          leftIcon={<Icon kind='AlertInfoFilled' />}
          size='medium'
          text={l('You’ve reached the max number of tiles allowed ({count}).', {
            count: MAX_SELECTION_COUNT,
          })}
        />
      )}
      {isError && (
        <InlineMessage
          colorTheme='critical'
          className='Nav_ShortcutsPersonalizationModal-footer-message'
          leftIcon={<Icon kind='AlertCriticalOutline' />}
          size='medium'
          text={l(
            'There was a problem saving your shortcuts. Refresh the page to retry.'
          )}
        />
      )}
      <Button
        className='Nav_ShortcutsPersonalizationModal-footer-defaultsButton'
        isDisabled={areActionsDisabled || (!isModified && isDefaultSelection)}
        type='button'
        kind='ghost'
        onClick={handleUseDefaultsClick}>
        {l('Revert to Default')}
      </Button>
      <Button
        isDisabled={areActionsDisabled || !isModified}
        type='button'
        onClick={handleSubmitClick}>
        {l('Save')}
      </Button>
    </FooterActionBar>
  );
}

function usePersistedDataEffect(
  savedPersonalShortcuts: PersonalShortcuts | undefined,
  selectedShortcutIds: SelectedShortcutId,
  defaultShortcutIds: MenuId[] | 'unauthorized' | undefined,
  isOpen: boolean,
  setSelectedShortcutIds: StateSetter<SelectedShortcutId>,
  setIsModified: StateSetter<boolean>,
  setIsDefaultSelection: StateSetter<boolean>
) {
  useEffect(() => {
    if (!isOpen) {
      if (selectedShortcutIds !== null) {
        // reset the state on close
        setSelectedShortcutIds(null);
        setIsDefaultSelection(false);
        setIsModified(false);
      }

      return;
    }

    if (
      // Don't need to run this effect once selectedShortcutIds is set
      selectedShortcutIds !== null ||
      UNUSABLE_PERSONAL_SHORTCUT_VALUES.includes(savedPersonalShortcuts) ||
      UNUSABLE_DEFAULT_SHORTCUT_VALUES.includes(defaultShortcutIds)
    ) {
      return;
    }

    const usableSavedPersonalShortcuts = savedPersonalShortcuts as Exclude<
      PersonalShortcuts,
      'error'
    >;
    const usableDefaultShortcutIds = defaultShortcutIds as MenuId[];

    setIsDefaultSelection(!usableSavedPersonalShortcuts.shortcutIds);
    setSelectedShortcutIds(
      new Set(
        usableSavedPersonalShortcuts.shortcutIds ?? usableDefaultShortcutIds
      )
    );
  }, [
    savedPersonalShortcuts,
    selectedShortcutIds,
    defaultShortcutIds,
    isOpen,
    setSelectedShortcutIds,
    setIsModified,
    setIsDefaultSelection,
  ]);
}
